import React, { useMemo, useRef, useState } from "react"
import { Checkbox, ConfigProvider, Modal, Segmented } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faFileExcel,
  faFilePowerpoint,
} from "@fortawesome/free-regular-svg-icons"
import ProgressModal from "shared/ui/ProgressModal/ProgressModal.component"
import { notify } from "shared/ui/Notifications/notify"
import { downloadWorkbook } from "utils/excel/exportTableModels"
import { isDataTile } from "features/Dashboard/types/dashboardDocument"
import {
  useDashboardStore,
  useDashboardStoreApi,
} from "features/Dashboard/runtime/dashboardStore"
import type { QueryQueue } from "features/Dashboard/runtime/queryQueue"
import {
  chosenDataTiles,
  collectExportTiles,
  ensureTilesReady,
  exportedTileCount,
  exportPanels,
} from "features/Dashboard/export/collectExportTiles"
import { buildExcelWorkbook } from "features/Dashboard/export/excelExport"
import { buildPptxDeck } from "features/Dashboard/export/pptxExport"
import { filterSummary } from "features/Dashboard/export/filterSummary"
import { WORKSPACE_THEME } from "features/Dashboard/components/workspaceTheme"
import DummyNotice from "features/DummyData/components/DummyNotice.component"
import { useQuestionnaireProjectId } from "features/DummyData/api/useDummyData"

type Format = "excel" | "pptx"
type SheetMode = "separate" | "combined"

const FORMAT_OPTIONS: { value: Format; label: React.ReactNode }[] = [
  {
    value: "excel",
    label: (
      <span className="dq-export__format">
        <FontAwesomeIcon icon={faFileExcel} />
        Excel
      </span>
    ),
  },
  {
    value: "pptx",
    label: (
      <span className="dq-export__format">
        <FontAwesomeIcon icon={faFilePowerpoint} />
        PowerPoint
      </span>
    ),
  },
]

const SHEET_OPTIONS: { value: SheetMode; label: string }[] = [
  { value: "separate", label: "One sheet per table" },
  { value: "combined", label: "One combined sheet" },
]

const countLabel = (n: number): string =>
  n === 0
    ? "No tables or charts"
    : n === 1
      ? "1 table or chart"
      : `${n} tables and charts`

/** The dashboard title without the characters a file name refuses. */
const fileName = (title: string): string =>
  title.replace(/[^\w\- ]+/g, "").trim() || "dashboard"

const warnSkipped = (n: number): void => {
  if (n > 0) notify.toast.warning(`${n} tile(s) had no data and were skipped.`)
}

/**
 * Exports the dashboard as the viewer sees it now (filters, cross-filters
 * and tile controls in force) to Excel or PowerPoint. Tiles of the chosen
 * pages that have no current result are run first, so pages never shown
 * export too. Pages are chosen by leaving them out, so a page added later
 * is in by default.
 */
const ExportModal: React.FC<{
  open: boolean
  onClose: () => void
  queue: QueryQueue | null
}> = ({ open, onClose, queue }) => {
  const store = useDashboardStoreApi()
  // A project without a questionnaire never has dummy data: nothing to read.
  const projectId = useDashboardStore((s) => s.projectId)
  const dummyFor = useQuestionnaireProjectId(projectId)
  const pages = useDashboardStore((s) => s.document.pages)
  const summary = useDashboardStore(filterSummary)
  const [format, setFormat] = useState<Format>("excel")
  const [sheetMode, setSheetMode] = useState<SheetMode>("separate")
  const [excluded, setExcluded] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  // Set at once, so a second click before the re-render does not export twice.
  const running = useRef(false)

  const chosen = pages.filter((p) => !excluded.includes(p.id)).map((p) => p.id)
  const counts = useMemo(
    () =>
      Object.fromEntries(
        pages.map((p) => [p.id, p.tiles.filter(isDataTile).length])
      ),
    [pages]
  )

  const run = async (): Promise<void> => {
    if (running.current) return
    if (!queue) {
      notify.error("Export", "The session is not ready yet.")
      return
    }
    running.current = true
    setBusy(true)
    try {
      await ensureTilesReady(store, queue, chosen)
      const state = store.getState()
      const entries = collectExportTiles(state, chosen)
      const current = filterSummary(state)
      const name = fileName(state.document.title)
      if (format === "excel") {
        if (entries.length === 0) {
          notify.error(
            "Nothing to export",
            "None of the chosen pages has a table or chart with results."
          )
          return
        }
        await downloadWorkbook(
          buildExcelWorkbook({
            entries,
            themeStyle: state.document.theme.tableStyle,
            mode: sheetMode,
            summary: current,
            title: state.document.title,
          }),
          name
        )
        warnSkipped(
          chosenDataTiles(state, chosen).length - exportedTileCount(entries)
        )
      } else {
        // A repeated tile's panels go in as a set; the rest one model each.
        const single = entries.filter((e) => !e.panel)
        const { deck, skipped } = buildPptxDeck({
          document: state.document,
          pageIds: chosen,
          models: Object.fromEntries(single.map((e) => [e.tile.id, e.model])),
          panels: exportPanels(entries),
          rawModels: Object.fromEntries(
            single.map((e) => [e.tile.id, e.rawModel ?? e.model])
          ),
          controls: Object.fromEntries(
            entries.map((e) => [e.tile.id, e.controls])
          ),
          summary: current,
        })
        await deck.writeFile({ fileName: `${name}.pptx` })
        warnSkipped(skipped.length)
      }
      notify.toast.success("Export ready")
      onClose()
    } catch (e) {
      notify.error(
        "Export failed",
        e instanceof Error ? e.message : "Unknown error"
      )
    } finally {
      running.current = false
      setBusy(false)
    }
  }

  return (
    <ConfigProvider theme={WORKSPACE_THEME}>
      <Modal
        open={open && !busy}
        onCancel={onClose}
        onOk={() => void run()}
        okText="Export"
        okButtonProps={{ disabled: chosen.length === 0 }}
        title="Export dashboard"
        width={480}
        rootClassName="dq-workspace dq-export-modal"
      >
        <DummyNotice projectId={dummyFor} className="mb-3" />
        <div className="dq-export">
          <span className="dq-export__label" id="dq-export-format">
            Format
          </span>
          <Segmented
            aria-labelledby="dq-export-format"
            className="dq-export__control"
            value={format}
            options={FORMAT_OPTIONS}
            onChange={(v) => setFormat(v)}
          />

          <span className="dq-export__label" id="dq-export-pages">
            Pages
          </span>
          <div
            role="group"
            aria-labelledby="dq-export-pages"
            className="dq-export__pages"
          >
            <Checkbox.Group
              className="dq-export__page-list"
              value={chosen}
              onChange={(ids) =>
                setExcluded(
                  pages.map((p) => p.id).filter((id) => !ids.includes(id))
                )
              }
            >
              {pages.map((p) => (
                <Checkbox key={p.id} value={p.id} className="dq-export__page">
                  <span className="dq-export__page-title">{p.title}</span>
                  <span className="dq-export__page-count">
                    {countLabel(counts[p.id] ?? 0)}
                  </span>
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>

          {format === "excel" && (
            <>
              <span className="dq-export__label" id="dq-export-sheets">
                Sheets
              </span>
              <Segmented
                aria-labelledby="dq-export-sheets"
                className="dq-export__control"
                value={sheetMode}
                options={SHEET_OPTIONS}
                onChange={(v) => setSheetMode(v)}
              />
            </>
          )}

          <p className="dq-export__note">
            Exports what the dashboard shows now.{" "}
            {summary ? (
              <span className="dq-export__filters">{summary}</span>
            ) : (
              "No filters are active."
            )}
          </p>
        </div>
      </Modal>
      <ProgressModal
        title="Preparing export"
        isLoading={busy}
        duration={8000}
      />
    </ConfigProvider>
  )
}

export default ExportModal
